import React from 'react';
import I18n from 'i18n-js';
import { get } from 'lodash';
import { Location } from 'history';

import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import URLHelpers from '../../shared/url_helpers';

import SellGrid, { renderGridItemsParams } from '../../sell/sell_grid';
import { CSPSearchInputV2 } from '../../csp_search/csp_search_input_v2';
import { reverb_search_CSPSearchRequest_Aggregation } from '@reverbdotcom/commons/src/gql/graphql';
import PriceGuideToolBailCard from './price_guide_tool_bail_card';
import PriceGuideToolCard from './price_guide_tool_card';
import PriceGuideToolInstructions from './price_guide_tool_instructions';
import PriceGuideToolPageWrapper from '../price_guide_tool_page_wrapper';

export const COMPONENT_NAME = 'PriceGuideToolIndex';

interface IProps {
  location: Location;
}

export function renderGridItems(ownProps: renderGridItemsParams) {
  const { csps, location } = ownProps;

  const cspCards = csps.map((csp, idx) => {
    return (
      <PriceGuideToolCard
        key={idx}
        csp={csp}
        index={idx}
        location={location}
      />
    );
  });

  if (csps.length > 0) {
    const { query } = location.query;
    cspCards.push(
      <PriceGuideToolBailCard
        key={csps.length}
        href={URLHelpers.marketplacePath({ query })}
      />,
    );
  }

  return cspCards;
}

export function renderMarketplaceLink(query: string) {
  return () => (
    <CoreLink
      to={URLHelpers.marketplacePath({ query })}
      className="button button--primary"
    >
      {I18n.t('discovery.priceGuideTool.index.search.marketplaceLinkText')}
    </CoreLink>
  );
}

export default function PriceGuideToolIndex({ location }: IProps) {
  const queryString = get(location, 'query.query', '');
  const hasQuery = !!queryString;

  const sanitizedLocation = {
    ...location,
    query: {
      ...location?.query,
      runDefaultQuery: false,
    },
  };

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
  }, true);

  return (
    <PriceGuideToolPageWrapper parentComponentName="PriceGuideToolIndex">
      <div className="g-container">
        <div className="g-col-6 g-col-tablet-12">
          <CSPSearchInputV2
            initialValue={queryString}
            submitPath={URLHelpers.priceGuideSearchPath}
            placeholder={I18n.t('discovery.priceGuideTool.index.search.placeholder')}
          />
        </div>
      </div>
      {hasQuery ? (
        <SellGrid
          location={sanitizedLocation}
          renderGridItems={renderGridItems}
          renderEmptyLink={renderMarketplaceLink(sanitizedLocation.query.query)}
          filterNames={[
            reverb_search_CSPSearchRequest_Aggregation.BRAND_NAMES,
            reverb_search_CSPSearchRequest_Aggregation.CATEGORY_UUIDS,
            reverb_search_CSPSearchRequest_Aggregation.DECADES,
            reverb_search_CSPSearchRequest_Aggregation.FINISHES,
          ]}
        />
      ) : (
        <PriceGuideToolInstructions />
      )}
    </PriceGuideToolPageWrapper>
  );
}
